import { Injectable, OnDestroy, signal } from '@angular/core';
import { TabSyncMessage } from './tab-sync.model';

@Injectable({
  providedIn: 'root',
})
export class TabSyncService implements OnDestroy {
  private readonly CHANNEL_NAME = 'pbpa_tab_sync_channel';
  private readonly STORAGE_ACTIVE_TAB_KEY = 'pbpa_current_active_tab_id';
  private readonly SESSION_TAB_ID_KEY = 'pbpa_session_tab_id';

  readonly tabId: string;
  private channel?: BroadcastChannel;

  // Signal indicating whether THIS tab is currently the active one
  readonly isCurrentTabActive = signal<boolean>(true);

  constructor() {
    this.tabId = this.resolveSessionTabId();
    this.initChannel();
    this.claimActiveTab();
  }

  /**
   * Generates or retrieves a unique tab id persisted only within this specific browser tab session.
   */
  private resolveSessionTabId(): string {
    let existingId = sessionStorage.getItem(this.SESSION_TAB_ID_KEY);
    if (!existingId) {
      existingId = `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem(this.SESSION_TAB_ID_KEY, existingId);
    }
    return existingId;
  }

  /**
   * Initializes the BroadcastChannel listener or falls back to storage events.
   */
  private initChannel(): void {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(this.CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<TabSyncMessage>) => {
        this.handleMessage(event.data);
      };
    }

    // Also listen to window storage events as fallback/cross-origin safety
    window.addEventListener('storage', this.handleStorageEvent);
  }

  private handleStorageEvent = (event: StorageEvent): void => {
    if (event.key === this.STORAGE_ACTIVE_TAB_KEY && event.newValue) {
      if (event.newValue !== this.tabId) {
        this.isCurrentTabActive.set(false);
      }
    }
  };

  /**
   * Process incoming messages from other tabs.
   */
  private handleMessage(message: TabSyncMessage): void {
    if (!message || message.tabId === this.tabId) {
      return;
    }

    switch (message.type) {
      case 'TAB_CLAIM_ACTIVE':
        // Another tab has claimed primary focus
        this.isCurrentTabActive.set(false);
        break;

      case 'TAB_PING':
        // If this tab is active, reply with PONG so new tab knows we are active
        if (this.isCurrentTabActive()) {
          this.postMessage({
            type: 'TAB_PONG',
            tabId: this.tabId,
            timestamp: Date.now(),
          });
        }
        break;

      case 'TAB_PONG':
        // Another active tab responded
        break;
    }
  }

  /**
   * Post message via BroadcastChannel.
   */
  private postMessage(message: TabSyncMessage): void {
    try {
      this.channel?.postMessage(message);
    } catch (e) {
      console.warn('[TabSyncService] Broadcast error:', e);
    }
  }

  /**
   * Claims this tab as the active one.
   * Notifies all other open tabs/windows to yield and display the inactive overlay.
   * Optionally reloads the current page to ensure fresh state/data.
   */
  claimActiveTab(reload = false): void {
    this.isCurrentTabActive.set(true);
    localStorage.setItem(this.STORAGE_ACTIVE_TAB_KEY, this.tabId);

    this.postMessage({
      type: 'TAB_CLAIM_ACTIVE',
      tabId: this.tabId,
      timestamp: Date.now(),
    });

    if (reload) {
      window.location.reload();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent);
    if (this.channel) {
      this.channel.close();
      this.channel = undefined;
    }
  }
}
